import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Header } from '@/components/site/Header';
import { Footer } from '@/components/site/Footer';
import { ScrollProgress } from '@/components/site/ScrollProgress';
import { Seo } from '@/components/site/Seo';
import { useLenis, scrollToTop, scrollToEl } from '@/hooks/useLenis';
import { motionOn, finePointer } from '@/lib/motion';

/** Dot + ring cursor; grows over links, shows a label over [data-cursor]. */
function Cursor() {
  const root = useRef(null);
  useEffect(() => {
    const el = root.current;
    if (!finePointer() || !motionOn() || !el) return undefined;
    const dot = el.querySelector('.nd-cursor__dot');
    const ring = el.querySelector('.nd-cursor__ring');
    const label = el.querySelector('.nd-cursor__label');
    gsap.set([dot, ring], { x: -100, y: -100 });
    const dX = gsap.quickTo(dot, 'x', { duration: 0.12, ease: 'power3' });
    const dY = gsap.quickTo(dot, 'y', { duration: 0.12, ease: 'power3' });
    const rX = gsap.quickTo(ring, 'x', { duration: 0.5, ease: 'power3' });
    const rY = gsap.quickTo(ring, 'y', { duration: 0.5, ease: 'power3' });
    const onMove = (e) => { dX(e.clientX); dY(e.clientY); rX(e.clientX); rY(e.clientY); };
    const onOver = (e) => {
      const lab = e.target.closest('[data-cursor]');
      const link = e.target.closest('a, button, [role="button"], input, textarea, select, label');
      el.classList.toggle('is-label', !!lab);
      el.classList.toggle('is-link', !lab && !!link);
      if (lab) label.textContent = lab.getAttribute('data-cursor');
    };
    const onLeave = () => gsap.set([dot, ring], { x: -100, y: -100 });
    window.addEventListener('pointermove', onMove, { passive: true });
    document.addEventListener('pointerover', onOver);
    document.documentElement.addEventListener('pointerleave', onLeave);
    return () => {
      window.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerover', onOver);
      document.documentElement.removeEventListener('pointerleave', onLeave);
    };
  }, []);
  return (
    <div ref={root} className="nd-cursor" aria-hidden>
      <div className="nd-cursor__ring"><span className="nd-cursor__label">View</span></div>
      <div className="nd-cursor__dot" />
    </div>
  );
}

export function Layout({ children }) {
  useLenis();
  const location = useLocation();
  const curtain = useRef(null);
  const first = useRef(true);

  useEffect(() => {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
  }, []);

  useLayoutEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) scrollToEl(el);
        else scrollToTop();
      });
    } else {
      scrollToTop();
    }

    // Curtain wipe on client-side route changes (skipped on first paint).
    if (first.current) { first.current = false; return undefined; }
    if (motionOn() && curtain.current) {
      gsap.fromTo(curtain.current, { y: 0, yPercent: 0 }, { y: 0, yPercent: -100, duration: 0.9, ease: 'expo.inOut', delay: 0.05 });
    }
    const t = setTimeout(() => ScrollTrigger.refresh(), 400);
    return () => clearTimeout(t);
  }, [location.pathname, location.hash]);

  return (
    <div className="min-h-screen flex flex-col bg-ink-bg text-ink-text">
      <a
        href="#main"
        className="fixed left-4 -top-16 z-[100] rounded-lg bg-white px-4 py-2.5 text-sm font-medium text-ink-bg transition-[top] duration-200 focus:top-4"
      >
        Skip to content
      </a>
      <Seo />
      <ScrollProgress />
      <Header />
      <main id="main" className="flex-1 pt-[76px]">{children}</main>
      <Footer />
      <div className="nd-grain" aria-hidden />
      <Cursor />
      <div ref={curtain} className="nd-curtain" aria-hidden />
    </div>
  );
}
